const NUMBER_SAMPLE = '123456789';
const VALIDATION_PRIORITY = [
  'pattern',
  'exactLength',
  'minLength',
  'maxLength',
  'alphanumeric',
  'numbersOnly',
  'lettersOnly',
];

const getRulePriority = (rule) => {
  const idx = VALIDATION_PRIORITY.indexOf(rule.id);
  return idx === -1 ? VALIDATION_PRIORITY.length : idx;
};

export const getFirstValidationError = (value, rules) => {
  const sorted = [...rules].sort((a, b) => getRulePriority(a) - getRulePriority(b));
  const failing = sorted.find((rule) => !rule.validator(value));
  return failing ? failing.message : null;
};

const nextSampleChar = (sample, count) => sample[count % sample.length];

export const getPatternExample = (pattern) => {
  if (!pattern) return '';

  let numberCount = 0;

  const result = [...pattern]
    .map((char) => {
      if (char === 'X') {
        const idx = numberCount;
        numberCount += 1;
        return nextSampleChar(NUMBER_SAMPLE, idx);
      }
      return char;
    })
    .join('');

  return `e.g. ${result}`;
};

export const formatIdentification = (identificationString, prevValue = '', pattern = 'XXX-XX-XXXX') => {
  // Get literal characters from pattern (alphanumeric chars that aren't placeholders)
  const literalChars = [...pattern].filter((char) => char !== 'X' && /[a-zA-Z0-9]/.test(char));

  // Create literalPositions map for efficient lookup
  const literalPositions = [...pattern].reduce((acc, char, i) => {
    if (char !== 'X' && /[a-zA-Z0-9]/.test(char)) {
      acc[i] = char;
    }
    return acc;
  }, {});

  // Clean user input
  const userInput = identificationString.replace(/[^a-zA-Z0-9]/g, '');

  // Count placeholders in pattern
  const maxPlaceholders = (pattern.match(/X/g) || []).length;

  // Apply the dynamic mask based on the pattern
  let formatted = '';
  let userInputIndex = 0;
  let patternIndex = 0;

  for (; patternIndex < pattern.length && userInputIndex < userInput.length; patternIndex += 1) {
    const currentPatternChar = pattern[patternIndex];

    if (currentPatternChar === 'X') {
      // This is a placeholder - insert user input
      if (userInputIndex < userInput.length) {
        formatted += userInput[userInputIndex];
        userInputIndex += 1;
      }
    } else if (literalPositions[patternIndex]) {
      // This is a literal character
      const literalChar = literalPositions[patternIndex];

      // Check if user typed this literal character at the current position
      if (userInputIndex < userInput.length && userInput[userInputIndex].toLowerCase() === literalChar.toLowerCase()) {
        // User typed the literal character - consume it and add to formatted
        formatted += literalChar;
        userInputIndex += 1;
      } else {
        // User didn't type the literal - add it automatically if there's more input coming
        const remainingUserInput = userInput.length - userInputIndex;
        const remainingPlaceholders = pattern.slice(patternIndex + 1).replace(/[^X]/g, '').length;

        if (remainingUserInput > 0 && remainingPlaceholders > 0) {
          formatted += literalChar;
        } else if (userInputIndex > 0) {
          // Add literal if we have characters before it
          formatted += literalChar;
        }
      }
    } else {
      // This is a separator character (like - or parentheses)
      const remainingUserInput = userInput.length - userInputIndex;
      const remainingPlaceholders = pattern.slice(patternIndex + 1).replace(/[^X]/g, '').length;
      const currentPatternIndex = patternIndex;
      const hasMoreLiterals = Object.keys(literalPositions).some((pos) => parseInt(pos, 10) > currentPatternIndex);

      if ((remainingUserInput > 0 && remainingPlaceholders > 0) || hasMoreLiterals) {
        formatted += currentPatternChar;
      }
    }
  }

  // Append any trailing literal/separator characters after all placeholders are filled,
  // but not when the user is actively deleting (prevents the suffix from snapping back)
  const isDeleting = identificationString.length < prevValue.length;

  if (!isDeleting) {
    while (patternIndex < pattern.length && pattern[patternIndex] !== 'X') {
      formatted += pattern[patternIndex];
      patternIndex += 1;
    }
  }

  // Handle case where user has more input than placeholders available
  const actualUserCharsUsed = userInput.replace(new RegExp(`[${literalChars.join('')}]`, 'gi'), '').length;

  if (actualUserCharsUsed > maxPlaceholders) {
    // Limit user input to available placeholders
    let limitedUserInput = '';
    let placeholderCount = 0;

    for (let i = 0; i < userInput.length && placeholderCount < maxPlaceholders; i += 1) {
      const char = userInput[i];
      if (literalChars.some((literal) => literal.toLowerCase() === char.toLowerCase())) {
        // This is a literal character - don't count against placeholder limit
        limitedUserInput += char;
      } else {
        // This is a regular character - count against placeholder limit
        limitedUserInput += char;
        placeholderCount += 1;
      }
    }

    // Re-run formatting with limited input
    return formatIdentification(limitedUserInput, prevValue, pattern);
  }

  return formatted;
};

export const getIdentificationValidationRules = (config) => {
  const { validations = [] } = config;

  if (validations.length === 0) return [];

  const validationMap = validations.reduce((acc, validation) => {
    acc[validation.type] = validation;
    return acc;
  }, {});

  const rules = [];

  // Add pattern format validation if pattern exists
  if (config.pattern) {
    rules.push({
      id: 'pattern',
      message: `Must match the required format: ${config.pattern}`,
      validator: (value) => {
        if (!value || value.trim().length === 0) return false;

        // Check if value matches the pattern structure exactly
        if (value.length !== config.pattern.length) return false;

        // Validate each position
        for (let i = 0; i < config.pattern.length; i += 1) {
          const patternChar = config.pattern[i];
          const valueChar = value[i];

          if (patternChar === 'X') {
            // X positions should have alphanumeric characters
            if (!/[a-zA-Z0-9]/.test(valueChar)) return false;
          } else if (/[a-zA-Z]/.test(patternChar)) {
            // Non-X positions should match exactly (case insensitive for letters)
            if (patternChar.toLowerCase() !== valueChar.toLowerCase()) return false;
          } else if (patternChar !== valueChar) return false;
        }

        return true;
      },
    });
  }

  const minLengthRule = validationMap.minLength;
  const maxLengthRule = validationMap.maxLength;
  const exactLengthRule = validationMap.exactLength;
  const alphaNumericRule = validationMap.alphaNumericOnly;
  const numbersOnlyRule = validationMap.numbersOnly;
  const lettersOnlyRule = validationMap.lettersOnly;

  if (minLengthRule && minLengthRule.enabled) {
    const minLength = minLengthRule.value || 3;
    rules.push({
      id: 'minLength',
      message: `Must be at least ${minLength} characters`,
      validator: (value) => (value || '').length >= minLength,
    });
  }

  if (maxLengthRule && maxLengthRule.enabled) {
    const maxLength = maxLengthRule.value || 10;
    rules.push({
      id: 'maxLength',
      message: `Must be no more than ${maxLength} characters`,
      validator: (value) => (value || '').length <= maxLength,
    });
  }

  if (exactLengthRule && exactLengthRule.enabled) {
    const exactLength = exactLengthRule.value || 0;
    rules.push({
      id: 'exactLength',
      message: `Must be exactly ${exactLength} characters`,
      validator: (value) => (value || '').length === exactLength,
    });
  }

  if (alphaNumericRule && alphaNumericRule.enabled) {
    rules.push({
      id: 'alphanumeric',
      message: 'Must contain only letters and numbers',
      validator: (value) => {
        if (!value || value.trim().length === 0) return false;
        const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
        return /^[a-zA-Z0-9]+$/.test(cleaned) && cleaned.length > 0;
      },
    });
  }

  if (numbersOnlyRule && numbersOnlyRule.enabled) {
    rules.push({
      id: 'numbersOnly',
      message: 'Must contain only numbers',
      validator: (value) => {
        if (!value || value.trim().length === 0) return false;
        const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
        return /^[0-9]+$/.test(cleanedValue) && cleanedValue.length > 0;
      },
    });
  }

  if (lettersOnlyRule && lettersOnlyRule.enabled) {
    rules.push({
      id: 'lettersOnly',
      message: 'Must contain only letters',
      validator: (value) => {
        if (!value || value.trim().length === 0) return false;
        const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
        return /^[a-zA-Z]+$/.test(cleanedValue) && cleanedValue.length > 0;
      },
    });
  }

  return rules;
};
